// ./src/components/generic/LoadingOverlay/LoadingOverlay.tsx
//
// App loading overlay. The visual styles live ONCE, as global `.als-*` classes
// in index.html's inline <style> (which also drives the instant pre-mount
// loader). This component reuses those exact classes, so the pre-mount loader
// and this one are always pixel-consistent — light festive background, white
// logo circle, coral→gold spinning arcs — from a single source of CSS.
import React from "react";

import { Logo } from "../Logo";

// ============================================
// Types
// ============================================

export interface LoadingOverlayProps {
  /** Whether the overlay is visible */
  visible: boolean;
  /** Optional text to display below the loading indicator */
  loadingText?: string;
  /** Z-index for the overlay */
  zIndex?: number;
  /** Show app version */
  showVersion?: boolean;
  /** Show logo */
  showLogo?: boolean;
}

// ============================================
// Main Component
// ============================================

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  visible,
  loadingText,
  zIndex = 200,
  showVersion = true,
  showLogo = true,
}) => {
  if (!visible) {
    return null;
  }

  return (
    <div className="als-overlay" style={{ zIndex }}>
      {/* Warm background glow */}
      <div className="als-bg-glow" />

      <div className="als-content">
        {/* Logo with progress ring */}
        {showLogo && (
          <div className="als-logo-container">
            <div className="als-ring-bg" />
            <div className="als-ring-arc" />
            <div className="als-ring-arc-secondary" />
            <div className="als-logo-glow" />
            <div className="als-logo-circle" />
            <div className="als-logo-img">
              <Logo size="xlarge" />
            </div>
          </div>
        )}

        {/* Text */}
        <div className="als-text-section">
          {loadingText && (
            <div className="als-loading-text">{loadingText}</div>
          )}

          {showVersion && (
            <div className="als-version-text">
              v{import.meta.env.VITE_APP_VERSION}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
